/** Client Assessment tab — options and payload helpers (mirrors backend). */

import { displayDateUS } from "../../utils/dateUtils";
import { normalizeTimeOption } from "./components/clinicalForms/nurseNotes/nurseVisitCommon";

/** User-facing title for the assessment tab print output. */
export const CLIENT_ASSESSMENT_TITLE = "Client Assessment";

/** Normalize stored date → MM/DD/YYYY for display and edit. */
export function normalizeScheduleDate(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  return displayDateUS(s) || s;
}

/** Normalize stored time → 12-hour AM/PM label (supports legacy HH:MM). */
export function normalizeScheduleTime(value) {
  if (value == null || value === "") return "";
  const normalized = normalizeTimeOption(value);
  if (normalized) return normalized;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return String(value).trim();
  let hour24 = Number(match[1]);
  const minute = match[2];
  if (hour24 < 0 || hour24 > 23) return "";
  const period = hour24 < 12 ? "AM" : "PM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12}:${minute} ${period}`;
}

export const DAYS_OF_SERVICE = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

export const ADL_OPTIONS = [
  "Bathing",
  "Toileting",
  "Transfers",
  "Dressing",
  "Feeding",
  "Bed Mobility",
  "Grooming",
  "Walking",
  "Medication Reminder",
  "Other",
];

export const IADL_OPTIONS = [
  "Meal Prep",
  "Housekeeping",
  "Transportation",
  "Laundry",
  "Shopping",
  "Other",
];

export const MOBILITY_OPTIONS = [
  "Cane",
  "Walker",
  "Wheelchair",
  "Hoyer Lift",
  "Gait Belt",
  "Bed Bound",
  "Requires Assist",
  "Other",
];

export const CONTINENCE_OPTIONS = [
  "Bladder Control",
  "Bowel Control",
  "Catheter",
  "Ostomy",
  "Incontinent",
  "Diaper/Pad",
  "Other",
];

export const BEHAVIOR_OPTIONS = [
  "Anxiety",
  "Depression",
  "Wandering",
  "Aggression",
  "Agitation",
  "Withdrawn",
  "Other",
];

export const COGNITIVE_OPTIONS = [
  "Alert",
  "Memory Loss",
  "Confused",
  "Oriented",
  "Disoriented",
  "Impaired Judgment",
  "Other",
];

export const HOME_TYPE_OPTIONS = [
  "Apartment",
  "Townhome",
  "House",
  "Other",
];

export const SAFETY_OPTIONS = [
  "Grab Bars",
  "Smoke Detector",
  "CO Detector",
  "Trip Hazards",
  "Stairs",
  "Fire Extinguisher",
  "Adequate Lighting",
  "Clutter-Free Pathways",
  "Pets",
  "Other",
];

export const OTHER_OPTION = "Other";
export const PETS_OPTION = "Pets";

export const ASSESSMENT_SECTIONS = [
  "scheduleOfCare",
  "adl",
  "iadl",
  "physical",
  "mental",
  "home",
  "safety",
];

const emptyCheckboxGroup = () => ({
  selected: [],
  otherDetails: "",
});

export const emptyAssessmentPayload = () => ({
  scheduleOfCare: {
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    daysOfService: [],
    totalHoursPerDay: "",
    totalHoursPerWeek: "",
  },
  adl: emptyCheckboxGroup(),
  iadl: emptyCheckboxGroup(),
  physical: {
    mobility: emptyCheckboxGroup(),
    continence: emptyCheckboxGroup(),
  },
  mental: {
    behavior: emptyCheckboxGroup(),
    cognitive: emptyCheckboxGroup(),
  },
  home: {
    homeType: "",
    apartmentFloor: "",
    elevatorAccessible: "",
    houseFloors: "",
    otherSpecify: "",
  },
  safety: {
    selected: [],
    petsDetails: "",
    otherDetails: "",
  },
});

export function mergeAssessmentPayload(raw) {
  const base = emptyAssessmentPayload();
  if (!raw || typeof raw !== "object") return base;

  const mergeGroup = (value, fallback) => {
    if (!value || typeof value !== "object") return fallback;
    return {
      selected: Array.isArray(value.selected)
        ? value.selected.filter((x) => typeof x === "string")
        : fallback.selected,
      otherDetails:
        typeof value.otherDetails === "string"
          ? value.otherDetails
          : fallback.otherDetails,
    };
  };

  const s = raw.scheduleOfCare || {};
  const h = raw.home || {};
  const p = raw.physical || {};
  const m = raw.mental || {};
  const safety = raw.safety || {};

  return {
    scheduleOfCare: {
      startDate: normalizeScheduleDate(s.startDate),
      endDate: normalizeScheduleDate(s.endDate),
      startTime: normalizeScheduleTime(s.startTime),
      endTime: normalizeScheduleTime(s.endTime),
      daysOfService: Array.isArray(s.daysOfService) ? s.daysOfService : [],
      totalHoursPerDay:
        s.totalHoursPerDay != null ? String(s.totalHoursPerDay) : "",
      totalHoursPerWeek:
        s.totalHoursPerWeek != null ? String(s.totalHoursPerWeek) : "",
    },
    adl: mergeGroup(raw.adl, base.adl),
    iadl: mergeGroup(raw.iadl, base.iadl),
    physical: {
      mobility: mergeGroup(p.mobility, base.physical.mobility),
      continence: mergeGroup(p.continence, base.physical.continence),
    },
    mental: {
      behavior: mergeGroup(m.behavior, base.mental.behavior),
      cognitive: mergeGroup(m.cognitive, base.mental.cognitive),
    },
    home: {
      homeType: h.homeType ?? "",
      apartmentFloor: h.apartmentFloor ?? "",
      elevatorAccessible: h.elevatorAccessible ?? "",
      houseFloors: h.houseFloors ?? "",
      otherSpecify: h.otherSpecify ?? "",
    },
    safety: {
      selected: Array.isArray(safety.selected) ? safety.selected : [],
      petsDetails: safety.petsDetails ?? "",
      otherDetails: safety.otherDetails ?? "",
    },
  };
}

/** Section-scoped save payload for PUT /patients/:id/assessment */
export function pickAssessmentSectionPayload(section, values) {
  switch (section) {
    case "scheduleOfCare":
      return values.scheduleOfCare;
    case "adl":
      return values.adl;
    case "iadl":
      return values.iadl;
    case "physical":
      return values.physical;
    case "mental":
      return values.mental;
    case "home":
      return values.home;
    case "safety":
      return values.safety;
    default:
      return null;
  }
}

export function toggleCheckboxValue(list, value) {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(value)
    ? arr.filter((x) => x !== value)
    : [...arr, value];
}
